import { motion } from "framer-motion";
import { Play, Users, Smartphone, Zap, ShieldCheck } from "lucide-react";

const LENS_IMG =
  "https://images.unsplash.com/photo-1535540878298-a155c6d065ef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwxfHxmaWxtJTIwc2V0JTIwY2FtZXJhJTIwZGFyayUyMGdsb3d8ZW58MHx8fHwxNzkyMDAzMDU5fDA&ixlib=rb-4.1.0&q=85";
const CINEMA_IMG =
  "https://images.unsplash.com/photo-1761998528613-a18866ce78ed?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA8Mzl8MHwxfHNlYXJjaHw0fHxkYXJrJTIwY2luZW1hdGljJTIwbW92aWUlMjBzdHJlYW1pbmclMjBzdGFnZSUyMGZpbG0lMjBsaWdodHxlbnwwfHx8MTc5MjAwMzA0N3ww&ixlib=rb-4.1.0&q=85";

const reveal = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.12, duration: 0.8, ease: [0.22, 1, 0.36, 1] },
  }),
};

const cardClass =
  "group relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.03] transition-colors duration-500 hover:border-crimson/40";

const About = () => (
  <section id="about" className="relative mx-auto max-w-7xl px-6 py-24 sm:py-32" data-testid="about-section">
    <motion.div variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }}>
      <p className="font-mono text-xs font-medium uppercase tracking-[0.25em] text-crimson">The Platform</p>
      <h2 className="mt-4 max-w-2xl font-heading text-2xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
        One app. Every frame. Every friend.
      </h2>
    </motion.div>

    <div className="mt-14 grid gap-5 md:grid-cols-3">
      <motion.div
        variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} custom={1}
        className={`${cardClass} min-h-[340px] md:col-span-2`}
        data-testid="feature-streaming-card"
      >
        <img src={LENS_IMG} alt="Cinema camera on a dark film set" className="absolute inset-0 h-full w-full object-cover opacity-40 transition-transform duration-700 group-hover:scale-105" />
        <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/50 to-transparent" />
        <div className="relative flex h-full flex-col justify-end p-8">
          <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl border border-crimson/40 bg-crimson/15">
            <Play size={18} className="text-crimson" fill="currentColor" />
          </div>
          <h3 className="font-heading text-xl font-semibold text-white sm:text-2xl">Cinematic Video Streaming</h3>
          <p className="mt-2 max-w-md text-sm leading-relaxed text-slate-400">
            Premieres, originals, and live events rendered in stunning 4K HDR — engineered to feel like a private theater in your pocket.
          </p>
        </div>
      </motion.div>

      <motion.div
        variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} custom={2}
        className={`${cardClass} min-h-[340px]`}
        data-testid="feature-social-card"
      >
        <img src={CINEMA_IMG} alt="Dark cinematic stage lights" className="absolute inset-0 h-full w-full object-cover opacity-35 transition-transform duration-700 group-hover:scale-105" />
        <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/50 to-transparent" />
        <div className="relative flex h-full flex-col justify-end p-8">
          <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl border border-white/15 bg-white/5">
            <Users size={18} className="text-white" />
          </div>
          <h3 className="font-heading text-xl font-semibold text-white sm:text-2xl">Social by Design</h3>
          <p className="mt-2 text-sm leading-relaxed text-slate-400">
            Follow creators, react in real time, and build your community around the stories you love.
          </p>
        </div>
      </motion.div>

      <motion.div
        variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} custom={3}
        className={`${cardClass} md:col-span-3`}
        data-testid="feature-ios-card"
      >
        <div className="relative flex flex-col gap-8 p-8 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl border border-white/15 bg-white/5">
              <Smartphone size={18} className="text-white" />
            </div>
            <h3 className="font-heading text-xl font-semibold text-white sm:text-2xl">Native to iOS</h3>
            <p className="mt-2 max-w-md text-sm leading-relaxed text-slate-400">
              Built from the ground up for iPhone — fluid gestures, instant playback, and deep system integration.
            </p>
          </div>
          <div className="flex gap-8 sm:gap-12">
            <div className="flex flex-col items-start gap-1.5">
              <Zap size={16} className="text-crimson" />
              <p className="font-heading text-lg font-bold text-white">Instant</p>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Playback</p>
            </div>
            <div className="flex flex-col items-start gap-1.5">
              <ShieldCheck size={16} className="text-crimson" />
              <p className="font-heading text-lg font-bold text-white">Private</p>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">By Default</p>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  </section>
);

export default About;
